import React, { useState, ReactNode } from 'react';
import Spinner from '@atlaskit/spinner';
import Form, { Field } from '@atlaskit/form';
import Button from '@atlaskit/button';
import Select from '@atlaskit/select';
import CheckCircle from '@atlaskit/icon/glyph/check-circle';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { token } from '@atlaskit/tokens';
import { AlertableSignalFlowInput } from '@atlassiansox/phobos-client-ts';

import { colors, flexSpace } from '@/styles';
import { CRUDableType } from '@/meta';
import {
  useFetchErrorController,
  useNavigationController,
  SignalFlowValidateContext,
  SignalFlowValidateResultType,
} from '@/controllers';
import {
  ui2Api,
  TeamFormData,
  ProductFormData,
  CapabilityFormData,
  SloFormData,
} from '@/components/composed/forms/data-mapper';
import { apis, apiResolve } from '@/services';

import CapabilityForm from './capability';
import CapabilitySLOForm from './slo';
import Product from './product';
import Team from './team';
import { formLayout, actionsLayout, formDrawerOffset } from './styles';
import FormErrors, { reduceFieldErrors } from './form-errors';
import { helperCopy } from './copy';
import { fetchSignalFlowAlertable, isSignalFlowValidated } from './slo/fields/utils';

const team: FieldOption<CRUDableType> = {
  label: 'Team',
  value: 'team',
};

const product: FieldOption<CRUDableType> = {
  label: 'Product',
  value: 'product',
};

const capability: FieldOption<CRUDableType> = {
  label: 'Capability',
  value: 'capability',
};

const capabilitySlo: FieldOption<CRUDableType> = {
  label: 'Capability SLO',
  value: 'slo',
};

const objectTypes = [team, product, capability, capabilitySlo];

type CreateProps = {
  initialType?: CRUDableType;
  initialCapability?: FieldOption<string>;
  onClose: () => void;
};

const Create: React.FC<CreateProps> = ({ initialType, initialCapability, onClose }) => {
  const [type, setType] = React.useState<CRUDableType>(initialType || 'slo');
  const [publicId, setPublicId] = React.useState<string | undefined>();
  const [validateError, setValidateError] = useState<SignalFlowValidateResultType>();
  const [isValidating, setIsValidating] = useState<boolean>(false);

  const queryClient = useQueryClient();
  const { onError } = useFetchErrorController();
  const { onNavigation } = useNavigationController();

  const mutation = useMutation((request: <T>() => Promise<T>) => apiResolve(request()), {
    onError,
    onSuccess: (_data: any, _variables, _context) => {
      setPublicId(_data.publicId);
      queryClient.invalidateQueries({ queryKey: [type] });
      // Also invalidate queries to reflect slo changes in search results
      queryClient.invalidateQueries({ queryKey: ['capability'] });
      queryClient.invalidateQueries({ queryKey: ['search'] });
    },
  });

  const isFormLoading = mutation.isLoading || isValidating;

  async function submitHandler(
    payload: TeamFormData | ProductFormData | CapabilityFormData | SloFormData
  ): Promise<object | undefined> {
    let body;
    let request;

    // Validate SLO SFx queries before submitting
    if (type === 'slo') {
      const { goodQuery, badQuery, objective, type: sloType } = payload as SloFormData;

      // Invalid submit
      // @see https://sentry.io/organizations/atlassian-2y/issues/3420107635/?project=5988855
      if (!sloType) return undefined;

      // Only validate when either good or bad query is populated
      if (goodQuery || badQuery) {
        if (validateError) setValidateError(undefined);
        setIsValidating(true);
        try {
          const validatePayload: AlertableSignalFlowInput = {
            goodQuery: goodQuery || '',
            badQuery: badQuery || '',
            objective: Number(objective || 0),
          };
          const err = await fetchSignalFlowAlertable(validatePayload);
          setValidateError(err);

          if (!isSignalFlowValidated(err)) {
            return undefined; // skip submitting
          }
        } catch (e) {
          onError(e);
          return undefined; // skip submitting
        } finally {
          setIsValidating(false);
        }
      }
    }

    // eslint-disable-next-line no-param-reassign
    if (type === 'team' || type === 'product') payload.name = payload.name.trim();

    switch (type) {
      case 'team':
        body = ui2Api.team(payload as TeamFormData);
        request = apis.team.createTeam.bind(apis.team, { teamInput: body });
        break;
      case 'product':
        body = ui2Api.product(payload);
        request = apis.product.createProduct.bind(apis.product, { productInput: body });
        break;
      case 'capability':
        body = ui2Api.capability(payload as CapabilityFormData);
        request = apis.capability.createCapability.bind(apis.capability, {
          capabilityInput: body,
        });
        break;
      case 'slo':
        body = ui2Api.slo(payload as SloFormData);
        request = apis.slo.createSLO.bind(apis.slo, { sloInput: body });
        break;
    }

    try {
      await mutation.mutateAsync(request);
      return undefined;
    } catch (error) {
      if (error.response && error.response.status === 400) {
        return reduceFieldErrors(await error.response.json());
      }
      return undefined;
    }
  }

  return (
    <div data-testid="create-form" css={formDrawerOffset}>
      {!mutation.isSuccess ? (
        <>
          <div css={[flexSpace]}>
            <h3>Create</h3>
            {helperCopy[type].discovery as ReactNode}
          </div>
          <Field<FieldOption<CRUDableType>> name="object">
            {({ fieldProps: { id, ...rest } }) => (
              <Select<any>
                // eslint-disable-next-line react/jsx-props-no-spreading
                {...rest}
                // NOTE: atlaskit doesn't pass through data-testid so this is
                // the best that we have
                id="objectInput"
                inputId={id}
                defaultValue={objectTypes.find(o => o.value === type)}
                onChange={field => setType(field.value)}
                options={objectTypes}
              />
            )}
          </Field>
          <Form<TeamFormData | ProductFormData | CapabilityFormData | SloFormData>
            onSubmit={submitHandler}
          >
            {({ formProps }) => (
              <form {...formProps} noValidate>
                <div css={formLayout}>
                  {type === 'capability' && <CapabilityForm isLoading={mutation.isLoading} />}
                  {type === 'slo' && (
                    <SignalFlowValidateContext.Provider
                      value={{ isValidating, error: validateError }}
                    >
                      <CapabilitySLOForm
                        isLoading={isFormLoading}
                        initialCapability={initialCapability}
                      />
                    </SignalFlowValidateContext.Provider>
                  )}
                  {type === 'product' && <Product isLoading={mutation.isLoading} />}
                  {type === 'team' && <Team isLoading={mutation.isLoading} />}
                  {mutation.error && <FormErrors />}
                </div>
                <div css={actionsLayout}>
                  <Button onClick={onClose}>Cancel</Button>
                  <Button
                    type="submit"
                    isDisabled={isFormLoading}
                    overlay={isFormLoading && <Spinner />}
                    appearance="primary"
                  >
                    Confirm
                  </Button>
                </div>
              </form>
            )}
          </Form>
        </>
      ) : (
        <div>
          <h2>
            Success{' '}
            <CheckCircle label="Success" primaryColor={token('color.icon.success', colors.G300)} />
          </h2>
          <p>
            The entity was successfully created. You should now see this reflected in the reporting
            UI.
          </p>
          <p>
            To create additional {type} or any other entity you can select the button below; or if
            you&apos;re all done, you can safely dismiss this drawer.
          </p>
          <div css={actionsLayout}>
            <Button onClick={() => mutation.reset()}>Create another</Button>
            <Button
              appearance="primary"
              onClick={() => {
                onClose();
                if (type === capabilitySlo.value) onNavigation(`/slo/${publicId}`);
              }}
            >
              Done
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default React.memo(Create);
